'use es6';

import { getHsformattingSettings } from '../utils';
import { hsformattingConfig } from '../constants/hsformatting';
import { CALLOUT_SELECTOR } from '../constants/hscallout';
import { trackInteraction } from '../utils/usageTracking';
import { ESCAPE_BLOCK_FORMAT } from '../constants/usageTracking';
import { BOGUS_BR, isEmptyParagraph } from '../utils/dom';
import { isLinebreakNode, isNestedFirstChild, isNestedLastChild, clearRngContent } from './utils';
const ENTER_KEY = 13;
const CARRIAGE_RETURN_LIMIT = 3;
const TEXT_NODE = '#text';

// Escape out of a codeblock/pre tag when the user hits a carriage returns and
// makes [n=3] consecutive <br> tags. If this occurs within a codeblock, the block
// is split into {before} & {after} blocks, and a blank <p> tag is inserted and
// selected where appropriate (before, in the middle, or after the initial block).
function codeblockKeyboardEscape(editor, parentCodeblock, evt) {
  const bookmark = editor.selection.getBookmark();
  const bookmarkNode = editor.dom.get(`${bookmark.id}_start`);
  if (bookmarkNode.nextSibling && bookmarkNode.nextSibling.nodeName === TEXT_NODE && bookmarkNode.nextSibling.nodeValue) {
    editor.selection.moveToBookmark(bookmark);
    return;
  }

  // Check for [n] previous carriage returns
  let curNode = bookmarkNode;
  let counter = 0;
  while (counter < CARRIAGE_RETURN_LIMIT && curNode.previousSibling && curNode.previousSibling.nodeName === 'BR') {
    curNode = curNode.previousSibling;
    counter++;
  }
  if (counter === CARRIAGE_RETURN_LIMIT) {
    let nextNode;
    while (curNode !== bookmarkNode) {
      nextNode = curNode.nextSibling;
      editor.dom.remove(curNode);
      curNode = nextNode;
    }
    const parentBlock = editor.dom.getParent(bookmarkNode, 'strong,em,span:not([data-mce-type="bookmark"])', parentCodeblock);
    if (parentBlock) {
      const placeholder = editor.dom.create('br');
      editor.dom.insertAfter(placeholder, parentBlock);
      curNode = placeholder;
    }
    const rng = editor.selection.getRng();

    // Clear content after bookmark in current codeblock
    clearRngContent(editor, rng, curNode, parentCodeblock.lastChild);

    // Add new <p> block
    const newBlock = editor.dom.create('p', {}, BOGUS_BR);
    editor.dom.insertAfter(newBlock, parentCodeblock);
    editor.selection.setCursorLocation(newBlock, newBlock.childNodes.length);

    // Cleanup bookmark if needed
    if (bookmarkNode.parentNode) {
      bookmarkNode.parentNode.removeChild(bookmarkNode);
    }
    evt.preventDefault();
    trackInteraction(editor, 'tinymceKeyboardEscape', ESCAPE_BLOCK_FORMAT, {
      format: 'codeblock'
    });
    return;
  }
  editor.selection.moveToBookmark(bookmark);
}

// Escape out of a blockquote if the user hits a carriage return to make
// [n=3] consecutive <br> tags.
function blockquoteKeyboardEscape(editor, parentBlockquote, evt) {
  const rng = editor.selection.getRng();
  const selectedEndNode = editor.dom.getParent(rng.endContainer, 'p,h1,h2,h3,h4,h5,h6', parentBlockquote);

  // Check for [n=3] previous carriage returns
  let curNode = selectedEndNode;
  let counter = 0;
  while (counter < CARRIAGE_RETURN_LIMIT && curNode) {
    if (!isLinebreakNode(curNode)) break;
    counter++;
    if (counter < CARRIAGE_RETURN_LIMIT) {
      curNode = curNode.previousSibling;
    }
  }

  // Remove the extra carriage returns and un-blockquote the last line
  if (counter === CARRIAGE_RETURN_LIMIT) {
    let nextNode;
    while (curNode !== selectedEndNode) {
      nextNode = curNode.nextSibling;
      editor.dom.remove(curNode);
      curNode = nextNode;
    }
    editor.selection.select(selectedEndNode);
    editor.execCommand('hsblockquote');
    editor.selection.setCursorLocation(selectedEndNode);
    evt.preventDefault();
    trackInteraction(editor, 'tinymceKeyboardEscape', ESCAPE_BLOCK_FORMAT, {
      format: 'blockquote'
    });
  }
}

// Escape out of a callout when the user hits a carriage returns and
// makes [n=3] consecutive empty <p> tags.
function calloutKeyboardEscape(editor, parentCallout, evt) {
  const bookmark = editor.selection.getBookmark();
  // Getting the parent <p> for the bookmark
  const bookmarkNode = editor.dom.get(`${bookmark.id}_start`);
  const parentBookmarkNode = editor.dom.getParent(bookmarkNode, 'p', parentCallout);
  editor.selection.moveToBookmark(bookmark);

  // Check for [n] previous carriage returns
  // we need to get the parent <p>
  let curNode = parentBookmarkNode;
  // Starting from 1 since if we've got here,
  // parentBookmarkNode is already an empty paragraph
  let counter = 1;
  while (isEmptyParagraph(curNode) && counter < CARRIAGE_RETURN_LIMIT && curNode.previousSibling && isEmptyParagraph(curNode.previousSibling) &&
  // We're keeping consistency: you should always need to hit "enter" three times
  // so this condition apply the same logic for empty callout
  !isNestedFirstChild(curNode.previousSibling, parentCallout)) {
    curNode = curNode.previousSibling;
    counter++;
  }
  if (counter === CARRIAGE_RETURN_LIMIT) {
    let nextNode;
    while (curNode !== parentBookmarkNode) {
      nextNode = curNode.nextSibling;
      editor.dom.remove(curNode);
      curNode = nextNode;
    }

    // Add new <p> block
    const newBlock = editor.dom.create('p', {}, BOGUS_BR);
    editor.dom.insertAfter(newBlock, parentCallout);
    editor.selection.setCursorLocation(newBlock, newBlock.childNodes.length);

    // Cleanup bookmark parent if needed
    if (parentBookmarkNode.parentNode) {
      parentBookmarkNode.parentNode.removeChild(parentBookmarkNode);
    }
    evt.preventDefault();
    trackInteraction(editor, 'tinymceKeyboardEscape', ESCAPE_BLOCK_FORMAT, {
      format: 'callout'
    });
    return;
  }
  editor.selection.moveToBookmark(bookmark);
}

// When the RETURN key is pressed from inside a <center> tag, it should create
// a new <p> tag instead of propagating more empty <center> tags
// (or add a <br> if it's within another block element)
function initCenterTagKeyboardEscape(editor) {
  editor.on('init', () => {
    editor.on('KeyDown', evt => {
      if (evt.keyCode !== ENTER_KEY || !editor.selection.isCollapsed()) {
        return;
      }
      const centerNode = editor.dom.getParent(editor.selection.getNode(), 'center', editor.getBody());
      if (!centerNode) return;
      const rng = editor.selection.getRng();
      const atStart = rng.startOffset === 0;
      const atEnd = rng.endOffset === rng.endContainer.childNodes.length;
      if (!atStart && !atEnd) return;
      const newlineNode = centerNode.parentNode === editor.getBody() ? editor.dom.create('p', {}, BOGUS_BR) : editor.dom.create('br');
      if (atStart) {
        // Insert newline before <center> tag and keep cursor where it is
        centerNode.parentNode.insertBefore(newlineNode, centerNode);
      } else if (atEnd) {
        // Insert newline after <center> tag and put cursor there
        editor.dom.insertAfter(newlineNode, centerNode);
        editor.selection.select(newlineNode);
        editor.selection.collapse(true);
      }
      evt.preventDefault();
    }, true);
  });
}
export function initKeyboardEscapePlugin(editor) {
  const formats = getHsformattingSettings(editor.settings, hsformattingConfig.KEYBOARD_ESCAPE_FORMATS) || [];
  if (!formats.length) return;
  if (formats.includes('center')) {
    initCenterTagKeyboardEscape(editor);
  }
  const codeblockEscape = formats.includes('pre');
  const blockquoteEscape = formats.includes('blockquote');
  const calloutEscape = formats.includes(CALLOUT_SELECTOR);
  const selector = formats.join(',');
  if (codeblockEscape || blockquoteEscape || calloutEscape) {
    editor.on('init', () => {
      editor.on('KeyDown', evt => {
        if (evt.keyCode !== ENTER_KEY || !editor.selection.isCollapsed()) {
          return;
        }
        const parentBlock = editor.dom.getParent(editor.selection.getNode(), selector, editor.getBody());
        if (!parentBlock) return;
        const bookmark = editor.selection.getBookmark();
        const bookmarkNode = editor.dom.get(`${bookmark.id}_start`);

        // Bookmark node's next sibling should be a <br> if at the end of the block
        const atEndOfBlock = bookmarkNode.nextSibling && isNestedLastChild(bookmarkNode.nextSibling, parentBlock);
        bookmarkNode.parentNode.removeChild(bookmarkNode);
        if (!atEndOfBlock) return;
        if (codeblockEscape && parentBlock.nodeName === 'PRE') {
          codeblockKeyboardEscape(editor, parentBlock, evt);
        }
        if (blockquoteEscape && parentBlock.nodeName === 'BLOCKQUOTE') {
          blockquoteKeyboardEscape(editor, parentBlock, evt);
        }
        if (calloutEscape && editor.dom.is(parentBlock, CALLOUT_SELECTOR)) {
          calloutKeyboardEscape(editor, parentBlock, evt);
        }
      });
    });
  }
}